/**
 * LegacyPflegeRedirect — Kompatibilitätsroute für alte Deep-Links.
 *
 * Vor dem 23.09.2026 verschickte Links haben die Form
 * `/pflege/{accountId}/{profileId}[/{tab}]` (Konto-ID + Profil-ID). Seit der
 * Umstellung auf die kanonische Form `/pflege/{userId}[/{tab}]` (users.id
 * führt überall — Entscheidung Damon) müssen solche Links weiter funktionieren:
 * diese Komponente löst `profileId` → `user_id` auf (GET
 * /v2/admin/profiles/{id}, liefert `user_id` bereits mit) und ersetzt die
 * URL per `history.replaceState` (kein zusätzlicher Back-Stack-Eintrag).
 *
 * Hat die Person kein eigenes users-Konto (Alt-Kontakt, `user_id` null),
 * gibt es keine kanonische User-Route dafür — Fallback ist die
 * Konto-only-Ansicht `/pflege/konto/{accountId}`, statt auf einer toten
 * Route zu landen.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v2Members } from '../../../lib/familyAccounts';
import type { PageId } from '../../../lib/route';

interface Props {
  accountId: number;
  profileId: number;
  tabSlug: string | null;
  dark: boolean;
  navigate: (
    page: PageId,
    sub?: string | null,
    tab?: string | null,
    extra?: string | null,
    opts?: { replace?: boolean },
  ) => void;
}

export function LegacyPflegeRedirect({ accountId, profileId, tabSlug, dark, navigate }: Props) {
  const { t: tr } = useTranslation();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    v2Members.get(profileId)
      .then(member => {
        if (!alive) return;
        if (member.user_id != null) {
          navigate('pflege', String(member.user_id), tabSlug, null, { replace: true });
        } else {
          navigate('pflege', 'konto', String(accountId), null, { replace: true });
        }
      })
      .catch(() => {
        if (!alive) return;
        // Auflösung fehlgeschlagen (Profil gelöscht/Netzwerkfehler) — auf die
        // Konto-Ansicht ausweichen statt auf einer toten Route zu landen.
        setFailed(true);
        navigate('pflege', 'konto', String(accountId), null, { replace: true });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, profileId, tabSlug]);

  return (
    <div style={{ padding: 24, color: dark ? '#98a2b3' : '#667085', fontSize: 13 }}>
      {failed ? tr('pflege.detail.not_found') : tr('pflege.familienkonto.list.loading')}
    </div>
  );
}
